import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannelMulti } from "@/lib/realtime-channel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MessageSquare,
  AlertTriangle,
  Pin,
  Calendar,
  UserRound,
  ChevronDown,
  Trash2,
  Mail,
  Bot,
  CheckSquare,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  getPinnedConversationIds,
  togglePinnedConversationId,
} from "@/lib/conversation-pin-storage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { format, isToday, isYesterday, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  applyConversationListFilters,
  isConversationRelevantForListFilter,
  isUnassignedIntegrationFilter,
} from "@/lib/conversation-integration-assign";
import { resolveStatusScope } from "@/lib/conversation-filters";
import {
  CONVERSATION_LIST_SELECT,
  enrichConversationsList,
  enrichConversationsWithAssignedMemberLabels,
  mapRawConversationRow,
  sortConversationsByIdOrder,
  type MappedConversation,
} from "@/lib/conversation-list-map";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  computeConversationFilterCounts,
  isConversationVisibleToMember,
  matchesAttentionFilter,
  matchesStatusFilter,
  needsHandoffAttention,
  type ConversationAttentionFilter,
  type ConversationFilterCounts,
  type ConversationStatusFilter,
  type ConversationsVisibility,
  type MemberIntegrationAccessContext,
} from "@/lib/conversation-filters";
import { fetchInboxTabCounts } from "@/lib/inbox-stats";
import { INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT } from "@/lib/inbox-channel-counts";
import { fetchMemberIntegrationAccessContext } from "@/lib/member-accessible-integrations";
import { INBOX_SCOPE_CHANGED_EVENT } from "@/lib/inbox-scope-changed";
import {
  applyInboxFilterCountDelta,
  type InboxDeltaCounts,
  type InboxDeltaEvent,
} from "@/lib/inbox-filter-count-delta";
import {
  buildInboxKeysetOrFilter,
  mergeInboxKeysetPages,
} from "@/lib/conversation-inbox-keyset";
import {
  INBOX_DELTA_FETCH_LIMIT,
  INBOX_PROMOTION_BATCH_LIMIT,
  inboxTailCursor,
  mergeLoadedInboxRows,
  readInboxSortCursor,
  removeConversationFromLoaded,
  selectDeltaRowsForWindow,
  shouldPromoteUnloadedConversation,
} from "@/lib/conversation-inbox-window";
import { LeadTagBadge } from "@/components/leads/LeadTagBadge";
import {
  applyConversationLeadPatch,
  CONVERSATION_LEAD_PATCH_EVENT,
  type ConversationLeadPatch,
} from "@/lib/conversation-lead-patch";
import {
  applyConversationInboxPatch,
  CONVERSATION_INBOX_ROW_PATCH_EVENT,
  dispatchConversationInboxBadgeSync,
  patchConversationListFromUpdate,
  reorderConversationsByLastMessage,
  type ConversationInboxRowPatch,
} from "@/lib/conversation-inbox-patch";
import { useWorkspaceConversationRealtime } from "@/hooks/useWorkspaceConversationRealtime";
import { useInboxScopeInvalidation } from "@/hooks/useInboxScopeInvalidation";
import {
  markConversationRead,
  markConversationUnread,
} from "@/lib/conversation-member-read";
import { dispatchInboxScopeChanged } from "@/lib/inbox-scope-changed";
import { subscribeVisibilityPause } from "@/lib/realtime-visibility-pause";
import {
  REALTIME_PAUSE_ON_HIDDEN_ENABLED,
  getWorkspaceConversationHubHealth,
  type ConversationChangeHandler,
} from "@/lib/workspace-conversation-realtime-hub";
import { fetchInboxStatsHeartbeat } from "@/lib/inbox-stats";
import { isInboxStale, isRealtimeHubRecentlyActive } from "@/lib/inbox-staleness";
import {
  INBOX_DELTA_OVERLAP_MS,
  maxServerTimestamp,
  sinceWithOverlap,
  INBOX_HIDDEN_GRACE_MS,
  INBOX_REALTIME_HEALTHY_GRACE_MS,
  INBOX_STALE_CHECK_MS,
  createSingleFlight,
  inboxDeltaRowsToRefresh,
  shouldRunInboxSafetyCheck,
} from "@/lib/inbox-live-sync";
import { bindInboxSafetyLeader } from "@/lib/inbox-tab-leader";
import { fetchInboxPageRows } from "@/lib/fetch-inbox-page";
import type { LeadTag } from "@/types/lead";

const formatConversationDate = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, "HH:mm", { locale: ptBR });
  if (isYesterday(date)) return "Ontem";
  const daysDiff = differenceInDays(new Date(), date);
  if (daysDiff < 7) return format(date, "EEE", { locale: ptBR }).replace(/^\w/, c => c.toUpperCase());
  return format(date, "dd/MM/yy");
};

type Conversation = MappedConversation;

/** Contadores servidos pela RPC de inbox (unread é opcional em modos legados). */
type ServerFilterCounts = Pick<
  ConversationFilterCounts,
  "mine" | "attention" | "ia" | "all" | "open" | "closed"
> & { unread?: number };

interface ConversationListProps {
  workspaceId: string;
  channel: 'whatsapp' | 'instagram' | 'messenger';
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
  refreshKey?: number;
  searchTerm?: string;
  attentionFilter?: ConversationAttentionFilter;
  statusFilter?: ConversationStatusFilter;
  onCountsChange?: (counts: ConversationFilterCounts) => void;
  integrationId?: string | null;
  currentMemberId?: string | null;
  prioritizeUnread?: boolean;
  filterUpcomingAppointments?: boolean;
  filterCancelledAppointments?: boolean;
  filterUnreadOnly?: boolean;
  isAdmin?: boolean;
  conversationsVisibility?: ConversationsVisibility;
  selectedTagIds?: string[];
  onRequestClearContext?: (conversationId: string) => void;
  selectionMode?: boolean;
  selectedConversationIds?: Set<string>;
  onToggleConversationSelection?: (conversation: Conversation) => void;
  onEnterSelectionWithConversation?: (conversation: Conversation) => void;
  onSelectAllVisible?: (conversationIds: string[]) => void;
  onSelectedConversationsChange?: (conversations: Conversation[]) => void;
  onPinnedIdsChange?: (ids: Set<string>) => void;
  onVisibleSelectableIdsChange?: (ids: string[]) => void;
}


function stripMarkdown(text: string): string {
  return text
    .replace(/```([^`]+)```/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/~([^~]+)~/g, '$1');
}

const IA_STATE_INDICATORS = {
  active: {
    label: "Agente de IA",
    shell: "border-emerald-500/35 bg-emerald-500/14",
    icon: "text-emerald-600 dark:text-emerald-400",
  },
  paused: {
    label: "Agente de IA pausado",
    shell: "border-orange-500/35 bg-orange-500/14",
    icon: "text-orange-600 dark:text-orange-400",
  },
} as const;

const HANDOFF_BADGE = {
  label: "Aguardando humano",
  className: "bg-amber-500/15 text-amber-800 border border-amber-500/40 dark:text-amber-300",
};

export function ConversationList({
  workspaceId,
  channel,
  onSelectConversation,
  selectedConversationId,
  refreshKey,
  searchTerm = "",
  attentionFilter = "mine",
  statusFilter = "open",
  onCountsChange,
  integrationId,
  currentMemberId,
  prioritizeUnread = false,
  filterUpcomingAppointments = false,
  filterCancelledAppointments = false,
  filterUnreadOnly = false,
  isAdmin = false,
  conversationsVisibility = "workspace_inbox",
  selectedTagIds = [],
  onRequestClearContext,
  selectionMode = false,
  selectedConversationIds = new Set<string>(),
  onToggleConversationSelection,
  onEnterSelectionWithConversation,
  onSelectAllVisible,
  onSelectedConversationsChange,
  onPinnedIdsChange,
  onVisibleSelectableIdsChange,
}: ConversationListProps) {
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listTotalCount, setListTotalCount] = useState<number | null>(null);
  const [serverFilterCounts, setServerFilterCounts] = useState<
    | ServerFilterCounts
    | null
  >(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [integrationAccess, setIntegrationAccess] = useState<MemberIntegrationAccessContext | null>(
    null,
  );
  const integrationAccessRef = useRef<MemberIntegrationAccessContext | null>(null);
  const [integrationAccessResolved, setIntegrationAccessResolved] = useState(false);
  const [integrationAccessFailed, setIntegrationAccessFailed] = useState(false);

  const PAGE_SIZE = 50;
  const SEARCH_MIN_CHARS = 2;
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconcileFromBackgroundRef = useRef<() => void>(() => {});
  const lastSyncAtRef = useRef<string | null>(null);
  /** Maior updated_at visto do servidor. Base da busca de lacuna (o relógio do PC pode estar adiantado). */
  const serverWatermarkRef = useRef<string | null>(null);
  const leadPatchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const hasMoreRef = useRef(false);
  const listEpochRef = useRef(0);
  const promoteIdsRef = useRef<Set<string>>(new Set());
  const promoteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSearchActiveRef = useRef(false);
  const trimmedSearch = searchTerm.trim();
  const isSearchActive = trimmedSearch.length >= SEARCH_MIN_CHARS;
  // Escopo abertas/encerradas — aplicado em todas as abas, inclusive "Todas".
  const statusScope = resolveStatusScope(attentionFilter, statusFilter);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isSearchActiveRef.current = isSearchActive;
  }, [isSearchActive]);
  
  const storageKey = `conversation-read-timestamps-${workspaceId}`;
  const pinnedStorageKey = `pinned-conversations-${workspaceId}`;
  const readTimestamps = useRef<Map<string, string>>(
    (() => {
      try {
        const saved = localStorage.getItem(`conversation-read-timestamps-${workspaceId}`);
        return saved ? new Map(Object.entries(JSON.parse(saved))) : new Map();
      } catch { return new Map(); }
    })()
  );
  const [pinnedConversationIds, setPinnedConversationIds] = useState<Set<string>>(
    () => getPinnedConversationIds(workspaceId),
  );

  useEffect(() => {
    setPinnedConversationIds(getPinnedConversationIds(workspaceId));
  }, [pinnedStorageKey, workspaceId]);

  const updatePinnedIds = useCallback(
    (next: Set<string>) => {
      setPinnedConversationIds(next);
      onPinnedIdsChange?.(next);
    },
    [onPinnedIdsChange],
  );

  const togglePinnedConversation = useCallback((conversationId: string) => {
    setPinnedConversationIds((current) => {
      const next = togglePinnedConversationId(workspaceId, conversationId, current);
      onPinnedIdsChange?.(next);
      return next;
    });
  }, [workspaceId, onPinnedIdsChange]);

  const markConversationAsUnread = useCallback(
    async (conv: Conversation) => {
      readTimestamps.current.delete(conv.conversation_id);
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify(Object.fromEntries(readTimestamps.current)),
        );
      } catch {
        // ignore
      }

      const wasRead = !(conv.unreadCount && conv.unreadCount > 0);
      const nextUnread = Math.max(1, conv.unreadCount || 0);
      setConversations((prev) =>
        prev.map((c) =>
          c.conversation_id === conv.conversation_id
            ? { ...c, isUnread: true, unreadCount: nextUnread }
            : c,
        ),
      );

      const { error } = await markConversationUnread(conv.conversation_id, {
        minUnread: nextUnread,
      });

      if (error) {
        toast({
          title: "Erro ao marcar como não lida",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (wasRead) {
        setServerFilterCounts((prev) => {
          if (!prev || prev.unread == null) return prev;
          return { ...prev, unread: prev.unread + 1 };
        });
      }

      window.dispatchEvent(
        new CustomEvent("conversation-unread", {
          detail: { conversationId: conv.conversation_id },
        }),
      );
      toast({ title: "Marcada como não lida" });
    },
    [storageKey, toast],
  );

  const isVisibleToMember = useCallback(
    (conv: Conversation) =>
      isConversationVisibleToMember(conv, {
        isAdmin,
        currentMemberId,
        conversationsVisibility,
        integrationAccess,
      }),
    [isAdmin, currentMemberId, conversationsVisibility, integrationAccess],
  );

  useEffect(() => {
    integrationAccessRef.current = integrationAccess;
  }, [integrationAccess]);

  useEffect(() => {
    if (!workspaceId) {
      setIntegrationAccess(null);
      setIntegrationAccessResolved(false);
      setIntegrationAccessFailed(false);
      return;
    }

    let cancelled = false;
    const load = () => {
      setIntegrationAccessFailed(false);
      void fetchMemberIntegrationAccessContext(workspaceId)
        .then((ctx) => {
          if (cancelled) return;
          setIntegrationAccess(ctx);
          setIntegrationAccessFailed(false);
          setIntegrationAccessResolved(true);
        })
        .catch(() => {
          if (cancelled) return;
          setIntegrationAccess(null);
          setIntegrationAccessFailed(true);
          setIntegrationAccessResolved(true);
        });
    };

    load();
    const onScope = () => load();
    window.addEventListener(INBOX_SCOPE_CHANGED_EVENT, onScope);
    window.addEventListener(INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT, onScope);
    return () => {
      cancelled = true;
      window.removeEventListener(INBOX_SCOPE_CHANGED_EVENT, onScope);
      window.removeEventListener(INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT, onScope);
    };
  }, [workspaceId]);

  /**
   * Conexões permitidas ao membro restrito. `null` = sem restrição (owner/membro
   * livre) → predicado da consulta permanece igual ao de hoje.
   * `[]` = restrito sem nenhuma conexão → nem chega a consultar o banco.
   */
  const allowedIntegrationIds = useMemo<string[] | null>(() => {
    const ctx = integrationAccess;
    if (!ctx || !ctx.workspaceRestricted || ctx.isOwner) return null;
    return [...ctx.allowedIntegrationIds].sort();
  }, [integrationAccess]);

  const allowedIntegrationIdsKey = allowedIntegrationIds ? allowedIntegrationIds.join(",") : "*";
  const allowedIntegrationIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    allowedIntegrationIdsRef.current = allowedIntegrationIds;
  }, [allowedIntegrationIdsKey, allowedIntegrationIds]);


  const matchesLoadedListFilters = useCallback(
    (conv: Conversation) => {
      if (!isVisibleToMember(conv)) return false;
      if (!matchesStatusFilter(conv, statusScope)) return false;
      if (!matchesAttentionFilter(conv, attentionFilter, currentMemberId)) return false;
      if (selectedTagIds.length > 0) {
        const tagIds = conv.tagIds || [];
        if (!selectedTagIds.some((id) => tagIds.includes(id))) return false;
      }
      if (filterUnreadOnly && !(conv.unreadCount && conv.unreadCount > 0)) return false;
      if (filterUpcomingAppointments || filterCancelledAppointments) {
        const matchesUpcoming = filterUpcomingAppointments && conv.hasUpcomingAppointment;
        const matchesCancelled = filterCancelledAppointments && conv.hasLastCancelledAppointment;
        if (!matchesUpcoming && !matchesCancelled) return false;
      }
      return true;
    },
    [
      isVisibleToMember,
      statusScope,
      attentionFilter,
      currentMemberId,
      selectedTagIds,
      filterUnreadOnly,
      filterUpcomingAppointments,
      filterCancelledAppointments,
    ],
  );

  const patchConversationsByLeadId = useCallback(
    (leadId: string, patch: Omit<ConversationLeadPatch, "leadId">) => {
      setConversations((prev) => {
        let changed = false;
        let needsCountRefresh = false;
        const next = prev.map((conv) => {
          if (conv.lead_id !== leadId) return conv;
          const before = conv;
          const updated = applyConversationLeadPatch(conv, { leadId, ...patch });
          if (!updated) return conv;
          changed = true;
          const applied = applyCountDeltaRef.current({
            type: "update",
            before,
            after: updated,
          });
          if (!applied) needsCountRefresh = true;
          return updated;
        });
        if (needsCountRefresh) {
          void refreshInboxCountsRef.current();
        }
        return changed ? next : prev;
      });
    },
    [],
  );

  const refreshLeadTagsInList = useCallback(
    async (leadId: string) => {
      const { data, error } = await supabase
        .from("lead_tag_assignments")
        .select("tag:tag_id ( id, name, color )")
        .eq("lead_id", leadId);

      if (error) {
        console.error("Error refreshing lead tags for inbox:", error);
        return;
      }

      const leadTags: LeadTag[] = [];
      for (const row of data ?? []) {
        const tagRaw = (row as { tag?: LeadTag | LeadTag[] | null }).tag;
        const tag = Array.isArray(tagRaw) ? tagRaw[0] : tagRaw;
        if (tag?.id) leadTags.push({ id: tag.id, name: tag.name, color: tag.color });
      }

      patchConversationsByLeadId(leadId, {
        leadTags,
        tagIds: leadTags.map((tag) => tag.id),
      });
    },
    [patchConversationsByLeadId],
  );

  const refreshLeadAssignmentInList = useCallback(
    async (leadId: string) => {
      const enriched = await enrichConversationsWithAssignedMemberLabels(supabase, [
        {
          conversation_id: "",
          channel: "",
          lead_id: leadId,
          lead_name: "",
          lead_phone: null,
          state: "",
          updated_at: "",
          followup_enabled: false,
          metadata: {},
        },
      ]);

      const row = enriched[0];
      patchConversationsByLeadId(leadId, {
        assignedMemberId: row?.assigned_member_id ?? null,
        assignedMemberIds: row?.assigned_member_ids ?? [],
        assignedMemberLabel: row?.assignedMemberLabel ?? null,
      });
    },
    [patchConversationsByLeadId],
  );

  useEffect(() => {
    const onLeadPatch = (event: Event) => {
      const detail = (event as CustomEvent<ConversationLeadPatch>).detail;
      if (!detail?.leadId) return;
      patchConversationsByLeadId(detail.leadId, detail);
    };

    const onInboxRowPatch = (event: Event) => {
      const patch = (event as CustomEvent<ConversationInboxRowPatch>).detail;
      if (!patch?.conversationId) return;
      setConversations((prev) => {
        const existing = prev.find((conv) => conv.conversation_id === patch.conversationId);
        let changed = false;
        const next = prev.map((conv) => {
          const updated = applyConversationInboxPatch(conv, patch, readTimestamps.current);
          if (updated) {
            changed = true;
            return updated;
          }
          return conv;
        });
        if (!changed || !existing) return prev;

        const after = next.find((conv) => conv.conversation_id === patch.conversationId) ?? null;
        const applied = applyCountDeltaRef.current({
          type: "update",
          before: existing,
          after,
        });
        if (!applied) {
          void refreshInboxCountsRef.current();
        }
        return next;
      });
    };

    window.addEventListener(CONVERSATION_LEAD_PATCH_EVENT, onLeadPatch);
    window.addEventListener(CONVERSATION_INBOX_ROW_PATCH_EVENT, onInboxRowPatch);
    return () => {
      window.removeEventListener(CONVERSATION_LEAD_PATCH_EVENT, onLeadPatch);
      window.removeEventListener(CONVERSATION_INBOX_ROW_PATCH_EVENT, onInboxRowPatch);
    };
  }, [patchConversationsByLeadId]);

  // Espelho síncrono da lista + handles estáveis para os ciclos de contadores.
  const conversationsRef = useRef<MappedConversation[]>([]);
  const refreshInboxCountsRef = useRef<() => Promise<void>>(async () => {});
  const applyCountDeltaRef = useRef<(event: InboxDeltaEvent) => boolean>(() => false);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const clearPromotionQueue = useCallback(() => {
    if (promoteTimerRef.current) {
      clearTimeout(promoteTimerRef.current);
      promoteTimerRef.current = null;
    }
    promoteIdsRef.current.clear();
  }, []);

  const flushPromotedRef = useRef<() => void>(() => {});

  const schedulePromotionFlush = useCallback(() => {
    if (promoteTimerRef.current) return;
    promoteTimerRef.current = setTimeout(() => {
      promoteTimerRef.current = null;
      flushPromotedRef.current();
    }, 300);
  }, []);

  const enqueuePromotion = useCallback(
    (conversationId: string) => {
      promoteIdsRef.current.add(conversationId);
      schedulePromotionFlush();
    },
    [schedulePromotionFlush],
  );

  const flushPromotedConversations = useCallback(async () => {
    if (!workspaceId || isSearchActiveRef.current) {
      promoteIdsRef.current.clear();
      return;
    }

    const queued = [...promoteIdsRef.current];
    if (queued.length === 0) return;

    if (allowedIntegrationIdsRef.current?.length === 0) {
      promoteIdsRef.current.clear();
      return;
    }

    const batch = queued.slice(0, INBOX_PROMOTION_BATCH_LIMIT);
    promoteIdsRef.current = new Set(queued.slice(INBOX_PROMOTION_BATCH_LIMIT));
    const epoch = listEpochRef.current;

    const pageRows = await fetchInboxPageRows(supabase, {
      workspaceId,
      channel,
      integrationId,
      isClosed: statusScope ? statusScope === "closed" : null,
      ids: batch,
      limit: batch.length,
    });

    let data: unknown[] | null = pageRows;
    if (!pageRows) {
      let query = supabase
        .from("conversations")
        .select(CONVERSATION_LIST_SELECT)
        .eq("workspace_id", workspaceId)
        .in("id", batch);

      query = applyConversationListFilters(
        query as any,
        channel,
        integrationId,
        allowedIntegrationIdsRef.current,
      ) as any;

      if (statusScope) {
        query = query.eq("is_closed", statusScope === "closed") as any;
      }

      const result = await query;
      if (epoch !== listEpochRef.current || isSearchActiveRef.current) return;
      if (result.error) {
        console.error("Error fetching promoted conversations:", result.error);
        if (epoch === listEpochRef.current && promoteIdsRef.current.size > 0) {
          schedulePromotionFlush();
        }
        return;
      }
      data = result.data;
    }

    if (epoch !== listEpochRef.current || isSearchActiveRef.current) return;

    const mapped = (data ?? [])
      .map((row) => mapRawConversationRow(row as Record<string, unknown>, readTimestamps.current))
      .filter(Boolean) as Conversation[];

    const enriched =
      mapped.length > 0 ? await enrichConversationsList(supabase, mapped, workspaceId) : [];
    if (epoch !== listEpochRef.current || isSearchActiveRef.current) return;

    const visible = enriched.filter((row) => matchesLoadedListFilters(row));
    if (visible.length > 0) {
      setConversations((prev) => {
        const merged = reorderConversationsByLastMessage(mergeLoadedInboxRows(prev, visible));
        conversationsRef.current = merged;
        return merged;
      });
    }

    if (promoteIdsRef.current.size > 0) schedulePromotionFlush();
  }, [workspaceId, channel, integrationId, statusScope, matchesLoadedListFilters, schedulePromotionFlush]);

  useEffect(() => {
    flushPromotedRef.current = () => {
      void flushPromotedConversations();
    };
  }, [flushPromotedConversations]);

  /**
   * Lista e contadores são ciclos separados:
   * - lista: patch local; busca só a conversa que entra na janela já carregada
   * - counts: delta local quando o snapshot é completo, senão uma RPC
   * Nunca refazer a lista só para atualizar badge.
   */
  const handleWorkspaceConversationChange = useCallback<ConversationChangeHandler>(
    (payload) => {
      const record = (payload.new || payload.old) as Record<string, unknown> | null;
      if (payload.eventType !== "DELETE" && typeof record?.updated_at === "string") {
        serverWatermarkRef.current = maxServerTimestamp(serverWatermarkRef.current, [
          record.updated_at,
        ]);
      }
      const isRelevant = isConversationRelevantForListFilter(record, channel, integrationId);
      if (!isRelevant) return;

      if (isSearchActive) {
        if (payload.eventType === "DELETE") {
          const conversationId =
            typeof (payload.old as { id?: unknown } | null)?.id === "string"
              ? (payload.old as { id: string }).id
              : null;
          if (!conversationId) return;
          const { next, removed } = removeConversationFromLoaded(
            conversationsRef.current,
            conversationId,
          );
          if (!removed) return;
          conversationsRef.current = next;
          setConversations(next);
          return;
        }

        if (payload.eventType === "UPDATE" && payload.new) {
          const row = payload.new as Record<string, unknown>;
          const conversationId = typeof row.id === "string" ? row.id : null;
          const prevList = conversationsRef.current;
          const existing = conversationId
            ? prevList.find((conv) => conv.conversation_id === conversationId)
            : undefined;

          if (!existing) return;

          const { next, changed } = patchConversationListFromUpdate(
            prevList,
            row,
            readTimestamps.current,
          );
          if (!changed) return;

          conversationsRef.current = next;
          setConversations(next);
          dispatchConversationInboxBadgeSync({ conversationId: conversationId ?? undefined });
        }
        return;
      }

      if (payload.eventType === "INSERT" && payload.new) {
        const row = payload.new as Record<string, unknown>;
        const conversationId = typeof row.id === "string" ? row.id : null;
        if (!conversationId) {
          void refreshInboxCountsRef.current();
          return;
        }

        const epoch = listEpochRef.current;
        void (async () => {
          const { data, error } = await supabase
            .from("conversations")
            .select(CONVERSATION_LIST_SELECT)
            .eq("id", conversationId)
            .maybeSingle();

          if (error || !data || epoch !== listEpochRef.current) {
            return;
          }

          const mapped = mapRawConversationRow(
            data as Record<string, unknown>,
            readTimestamps.current,
          );
          if (!mapped) {
            return;
          }

          const enriched = (
            await enrichConversationsList(supabase, [mapped], workspaceId)
          )[0];
          if (!enriched || !matchesLoadedListFilters(enriched) || epoch !== listEpochRef.current) {
            return;
          }

          const prevList = conversationsRef.current;
          const existing = prevList.find((conv) => conv.conversation_id === conversationId);
          const merged = reorderConversationsByLastMessage(
            existing
              ? prevList.map((conv) =>
                  conv.conversation_id === conversationId ? enriched : conv,
                )
              : [...prevList, enriched],
          );

          conversationsRef.current = merged;
          setConversations(merged);

          const applied = applyCountDeltaRef.current({
            type: existing ? "update" : "insert",
            before: existing ?? undefined,
            after: enriched,
          });
          if (!applied) void refreshInboxCountsRef.current();
        })();

        return;
      }

      if (payload.eventType === "UPDATE" && payload.new) {
        const row = payload.new as Record<string, unknown>;
        const conversationId = typeof row.id === "string" ? row.id : null;
        const prevList = conversationsRef.current;
        const existing = conversationId
          ? prevList.find((conv) => conv.conversation_id === conversationId)
          : undefined;

        // Fora da janela: busca só se a conversa entra nela. O resto não mexe na lista.
        if (!existing) {
          const cursor = conversationId ? readInboxSortCursor(row) : null;
          if (
            cursor &&
            shouldPromoteUnloadedConversation(
              {
                loadedCount: prevList.length,
                pageSize: PAGE_SIZE,
                hasMore: hasMoreRef.current,
                tail: inboxTailCursor(prevList),
              },
              cursor,
            )
          ) {
            enqueuePromotion(cursor.id);
          }
          void refreshInboxCountsRef.current();
          return;
        }

        const { next, changed } = patchConversationListFromUpdate(
          prevList,
          row,
          readTimestamps.current,
        );
        if (!changed) return;

        conversationsRef.current = next;
        setConversations(next);
        dispatchConversationInboxBadgeSync({ conversationId: conversationId ?? undefined });

        const after = next.find((conv) => conv.conversation_id === conversationId) ?? null;
        if (after && !matchesLoadedListFilters(after)) {
          const pruned = next.filter((conv) => conv.conversation_id !== conversationId);
          conversationsRef.current = pruned;
          setConversations(pruned);
        }

        const applied = applyCountDeltaRef.current({
          type: "update",
          before: existing,
          after,
        });
        if (!applied) void refreshInboxCountsRef.current();
        return;
      }

      if (payload.eventType === "DELETE") {
        const conversationId =
          typeof (payload.old as { id?: unknown } | null)?.id === "string"
            ? (payload.old as { id: string }).id
            : null;
        if (!conversationId) {
          void refreshInboxCountsRef.current();
          return;
        }

        const existing = conversationsRef.current.find(
          (conv) => conv.conversation_id === conversationId,
        );
        if (!existing) {
          void refreshInboxCountsRef.current();
          return;
        }

        const { next } = removeConversationFromLoaded(conversationsRef.current, conversationId);
        conversationsRef.current = next;
        setConversations(next);
        dispatchConversationInboxBadgeSync({ conversationId });

        const applied = applyCountDeltaRef.current({ type: "delete", before: existing });
        if (!applied) void refreshInboxCountsRef.current();
      }
    },
    [channel, integrationId, isSearchActive, workspaceId, matchesLoadedListFilters, enqueuePromotion],
  );

  // Reconcile ao retornar à aba: hub resume (pause-on-hidden=1) OU visibilitychange (flag=0).
  const handleHubReconcile = useCallback(() => {
    reconcileFromBackgroundRef.current();
  }, []);

  useWorkspaceConversationRealtime(
    workspaceId,
    handleWorkspaceConversationChange,
    Boolean(workspaceId),
    REALTIME_PAUSE_ON_HIDDEN_ENABLED ? handleHubReconcile : undefined,
  );

  useInboxScopeInvalidation(workspaceId, () => {
    reconcileFromBackgroundRef.current();
  });

  /**
   * Fallback anti-staleness (independente de WebSocket), silencioso:
   * com Realtime saudável e evento recente, nenhuma query é feita.
   * Caso contrário, compara o token do escopo atual (membro + canal) e reconcilia
   * só quando o servidor avançou sem evento local. Reconexão do hub já reconcilia.
   */
  useEffect(() => {
    if (!workspaceId || !currentMemberId || typeof window === "undefined") return;

    const STALE_GRACE_MS = 20_000;
    const leader = bindInboxSafetyLeader(
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `tab-${Date.now()}`,
    );

    const tick = async () => {
      const health = getWorkspaceConversationHubHealth(workspaceId);
      if (
        !shouldRunInboxSafetyCheck({
          documentHidden: typeof document !== "undefined" && document.hidden,
          isLeaderTab: leader.isLeader(),
          hubRecentlyActive: isRealtimeHubRecentlyActive({
            joined: health.joined,
            lastEventAt: health.lastEventAt,
            subscribedAt: health.subscribedAt,
            graceMs: INBOX_REALTIME_HEALTHY_GRACE_MS,
          }),
        })
      ) {
        return;
      }

      const lastLocal = Math.max(
        health.lastEventAt ?? 0,
        lastSyncAtRef.current ? Date.parse(lastSyncAtRef.current) || 0 : 0,
      );

      const heartbeat = await fetchInboxStatsHeartbeat(supabase, {
        workspaceId,
        memberId: currentMemberId,
        channel,
        integrationId,
      });
      if (
        isInboxStale({
          serverHeartbeatAt: heartbeat,
          lastLocalActivityAtMs: lastLocal,
          graceMs: STALE_GRACE_MS,
        })
      ) {
        reconcileFromBackgroundRef.current();
      }
    };

    const interval = window.setInterval(() => {
      void tick();
    }, INBOX_STALE_CHECK_MS);
    return () => {
      window.clearInterval(interval);
      leader.close();
    };
  }, [workspaceId, currentMemberId, channel, integrationId]);

  const countsInFlightRef = useRef<Promise<void> | null>(null);
  const lastCountsFetchAtRef = useRef(0);
  const serverFilterCountsRef = useRef<ServerFilterCounts | null>(null);

  const refreshInboxCounts = useCallback(async () => {
    if (!workspaceId || isSearchActive) return;

    const now = Date.now();
    if (now - lastCountsFetchAtRef.current < 1500 && countsInFlightRef.current) {
      return countsInFlightRef.current;
    }

    const run = async () => {
      lastCountsFetchAtRef.current = Date.now();
      try {
        const result = await fetchInboxTabCounts(supabase, {
          workspaceId,
          channel,
          integrationId,
          statusFilter: statusScope,
          memberId: currentMemberId,
        });
        const { listTotal, ...filterCounts } = result;
        setListTotalCount(listTotal);
        setServerFilterCounts(filterCounts);
        serverFilterCountsRef.current = filterCounts;
      } catch (error) {
        console.error("fetchInboxTabCounts:", error);
        setListTotalCount(null);
        setServerFilterCounts(null);
      }
    };

    const promise = run().finally(() => {
      if (countsInFlightRef.current === promise) {
        countsInFlightRef.current = null;
      }
    });
    countsInFlightRef.current = promise;
    return promise;
  }, [
    workspaceId,
    channel,
    integrationId,
    statusScope,
    currentMemberId,
    isSearchActive,
  ]);

  /**
   * Patch local dos contadores a partir de um evento realtime.
   * Retorna false quando o snapshot é incompleto — o chamador cai na RPC.
   */
  const applyCountDelta = useCallback(
    (event: InboxDeltaEvent): boolean => {
      if (isSearchActive) return true;

      const current = serverFilterCountsRef.current;
      if (!current) return false;

      const previous: InboxDeltaCounts = {
        mine: current.mine,
        attention: current.attention,
        ia: current.ia,
        all: current.all,
        open: current.open,
        closed: current.closed,
        unread: current.unread ?? 0,
      };

      const { counts, confident } = applyInboxFilterCountDelta(previous, event, {
        statusFilter: statusScope,
        currentMemberId,
        isAdmin,
        conversationsVisibility,
        integrationAccess,
      });
      if (!confident) return false;

      const visibleDelta =
        counts.open + counts.closed - (previous.open + previous.closed);

      serverFilterCountsRef.current = counts;
      setServerFilterCounts(counts);
      if (visibleDelta !== 0) {
        setListTotalCount((prev) => (prev == null ? prev : Math.max(0, prev + visibleDelta)));
      }
      return true;
    },
    [isSearchActive, statusScope, currentMemberId, isAdmin, conversationsVisibility, integrationAccess],
  );

  useEffect(() => {
    refreshInboxCountsRef.current = refreshInboxCounts;
  }, [refreshInboxCounts]);

  useEffect(() => {
    applyCountDeltaRef.current = applyCountDelta;
  }, [applyCountDelta]);

  useEffect(() => {
    serverFilterCountsRef.current = serverFilterCounts;
  }, [serverFilterCounts]);



  useEffect(() => {
    if (!loading) {
      const loadedCounts = computeConversationFilterCounts(conversations, currentMemberId, {
        isAdmin,
        conversationsVisibility,
        integrationAccess,
        statusFilter: statusScope,
      });

      if (serverFilterCounts && !isSearchMode) {
        onCountsChange?.({
          ...serverFilterCounts,
          unread: serverFilterCounts.unread ?? loadedCounts.unread,
        });
      } else {
        onCountsChange?.(loadedCounts);
      }
    }
  }, [
    conversations,
    loading,
    onCountsChange,
    currentMemberId,
    isAdmin,
    conversationsVisibility,
    integrationAccess,
    statusScope,
    serverFilterCounts,
    isSearchMode,
  ]);

  useEffect(() => {
    if (!workspaceId) return;
    // Espera o contexto de acesso antes de paginar: evita uma primeira página
    // com predicado incompleto (que seria descartada e recarregada depois).
    if (!integrationAccessResolved) return;
    const epoch = ++listEpochRef.current;

    if (integrationAccessFailed) {
      setLoadError("Não foi possível carregar suas permissões de conexão. Tente novamente.");
      setLoading(false);
      setConversations([]);
      return;
    }

    if (isSearchActive) {
      setIsSearchMode(true);
      setListTotalCount(null);
      setServerFilterCounts(null);
      setLoading(true);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

      const runSearch = async (requestId: number) => {
        const stillCurrent = () =>
          requestId === searchRequestIdRef.current && epoch === listEpochRef.current;
        try {
          const { data: ids, error: searchError } = await supabase.rpc(
            "search_conversations_inbox",
            {
              p_workspace_id: workspaceId,
              p_term: trimmedSearch,
              p_channel: channel,
              p_integration_id:
                integrationId && !isUnassignedIntegrationFilter(integrationId)
                  ? integrationId
                  : null,
              p_filter_unassigned: isUnassignedIntegrationFilter(integrationId),
              p_limit: 25,
            },
          );

          if (searchError) {
            console.error("search_conversations_inbox error:", searchError);
            if (stillCurrent()) {
              setConversations([]);
              setHasMore(false);
            }
            return;
          }

          const conversationIds = (ids as string[] | null) ?? [];
          if (conversationIds.length === 0) {
            if (stillCurrent()) {
              setConversations([]);
              setHasMore(false);
            }
            return;
          }

          const { data, error: fetchError } = await supabase
            .from("conversations")
            .select(CONVERSATION_LIST_SELECT)
            .in("id", conversationIds);

          if (fetchError) {
            console.error("Error fetching search conversations:", fetchError);
            return;
          }

          if (!stillCurrent()) return;

          const mapped = ((data ?? [])
            .map((row) =>
              mapRawConversationRow(row as Record<string, unknown>, readTimestamps.current),
            )
            .filter(Boolean) as Conversation[])
            .filter((row) =>
              isConversationRelevantForListFilter(row, channel, integrationId),
            );

          const enriched = await enrichConversationsList(supabase, mapped, workspaceId);
          if (!stillCurrent()) return;
          const ordered = sortConversationsByIdOrder(enriched, conversationIds);
          conversationsRef.current = ordered;
          setConversations(ordered);
          setHasMore(false);
        } finally {
          if (stillCurrent()) setLoading(false);
        }
      };

      searchDebounceRef.current = setTimeout(() => {
        void runSearch(++searchRequestIdRef.current);
      }, 300);

      return () => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        clearPromotionQueue();
      };
    }

    setIsSearchMode(false);
    searchRequestIdRef.current += 1;
    lastSyncAtRef.current = null;
    serverWatermarkRef.current = null;

    const fetchConversationsDelta = async (since: string) => {
      const pageRows = await fetchInboxPageRows(supabase, {
        workspaceId,
        channel,
        integrationId,
        isClosed: statusScope ? statusScope === "closed" : null,
        since,
        limit: INBOX_DELTA_FETCH_LIMIT,
      });

      let data: unknown[] | null = pageRows;
      if (!pageRows) {
        let query = supabase
          .from("conversations")
          .select(CONVERSATION_LIST_SELECT)
          .eq("workspace_id", workspaceId)
          .gte("updated_at", since);

        query = applyConversationListFilters(
          query as any,
          channel,
          integrationId,
          allowedIntegrationIdsRef.current,
        ) as any;

        if (statusScope) {
          query = query.eq("is_closed", statusScope === "closed") as any;
        }

        const result = await query
          .order("updated_at", { ascending: false })
          .limit(INBOX_DELTA_FETCH_LIMIT);

        if (result.error || epoch !== listEpochRef.current) {
          if (result.error) console.error("Error fetching conversation delta:", result.error);
          return;
        }
        data = result.data;
      }

      if (epoch !== listEpochRef.current) return;

      const mapped = (data ?? [])
        .map((row) => mapRawConversationRow(row as Record<string, unknown>, readTimestamps.current))
        .filter(Boolean) as Conversation[];
      serverWatermarkRef.current = maxServerTimestamp(
        serverWatermarkRef.current,
        mapped.map((row) => row.updated_at),
      );

      const selected = selectDeltaRowsForWindow(conversationsRef.current, mapped, {
        pageSize: PAGE_SIZE,
        hasMore: hasMoreRef.current,
      });
      const changed = inboxDeltaRowsToRefresh(conversationsRef.current, selected);

      if (changed.length === 0) {
        lastSyncAtRef.current = new Date().toISOString();
        return;
      }

      const enriched = await enrichConversationsList(supabase, changed, workspaceId);
      if (epoch !== listEpochRef.current) return;

      setConversations((prev) => {
        const merged = reorderConversationsByLastMessage(mergeLoadedInboxRows(prev, enriched));
        conversationsRef.current = merged;
        return merged;
      });
      lastSyncAtRef.current = new Date().toISOString();
    };

    const fetchConversations = async () => {
      try {
        // Membro restrito sem nenhuma conexão vinculada: nada a ler no banco.
        if (allowedIntegrationIdsRef.current?.length === 0) {
          if (epoch !== listEpochRef.current) return;
          setConversations([]);
          conversationsRef.current = [];
          hasMoreRef.current = false;
          setHasMore(false);
          setLoadError(null);
          setLoading(false);
          return;
        }

        const pageRows = await fetchInboxPageRows(supabase, {
          workspaceId,
          channel,
          integrationId,
          isClosed: statusScope ? statusScope === "closed" : null,
          limit: PAGE_SIZE + 1,
        });

        let data: unknown[] | null = pageRows;
        if (!pageRows) {
          let query = supabase
            .from("conversations")
            .select(CONVERSATION_LIST_SELECT)
            .eq("workspace_id", workspaceId);

          query = applyConversationListFilters(
            query as any,
            channel,
            integrationId,
            allowedIntegrationIdsRef.current,
          ) as any;
          if (statusScope) {
            query = query.eq("is_closed", statusScope === "closed") as any;
          }

          const result = await query
            .order("last_message_at", { ascending: false, nullsFirst: false })
            .order("id", { ascending: false })
            .limit(PAGE_SIZE + 1);

          if (result.error) {
            if (epoch !== listEpochRef.current) return;
            console.error("Error fetching conversations:", result.error);
            setLoadError("Não foi possível carregar as conversas.");
            setLoading(false);
            return;
          }
          data = result.data;
        }

        const mappedConversations = (data ?? [])
          .map((row) => mapRawConversationRow(row as Record<string, unknown>, readTimestamps.current))
          .filter(Boolean) as Conversation[];

        serverWatermarkRef.current = maxServerTimestamp(
          serverWatermarkRef.current,
          mappedConversations.map((row) => row.updated_at),
        );
        const pageConversations =
          mappedConversations.length > PAGE_SIZE
            ? mappedConversations.slice(0, PAGE_SIZE)
            : mappedConversations;
        const enriched = await enrichConversationsList(supabase, pageConversations, workspaceId);
        if (epoch !== listEpochRef.current) return;

        const nextHasMore = mappedConversations.length > PAGE_SIZE;
        hasMoreRef.current = nextHasMore;
        setHasMore(nextHasMore);
        setConversations(enriched);
        conversationsRef.current = enriched;
        lastSyncAtRef.current = new Date().toISOString();
        setLoadError(null);
        setLoading(false);
        // Counts têm ciclo próprio (efeito dedicado + delta realtime).
      } catch (err) {
        if (epoch !== listEpochRef.current) return;
        console.error("Error fetching conversations:", err);
        setLoadError("Não foi possível carregar as conversas.");
        setLoading(false);
      }
    };

    setLoadError(null);
    setLoading(true);
    fetchConversations();

    const runReconcile = createSingleFlight(async () => {
      if (lastSyncAtRef.current) {
        // Horário do servidor, não do PC: com relógio adiantado a busca pulava conversas.
        const since =
          sinceWithOverlap(serverWatermarkRef.current, INBOX_DELTA_OVERLAP_MS) ??
          new Date(new Date(lastSyncAtRef.current).getTime() - 5000).toISOString();
        await fetchConversationsDelta(since);
      } else {
        await fetchConversations();
      }
    });

    const reconcileFetch = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        void runReconcile();
      }, 300);
    };
    const channelName = integrationId
      ? `conversations-lead-tags-int-${integrationId}`
      : `conversations-lead-tags-${channel}`;

    const debouncedLeadPatch = (fn: () => void) => {
      if (leadPatchDebounceRef.current) clearTimeout(leadPatchDebounceRef.current);
      leadPatchDebounceRef.current = setTimeout(fn, 400);
    };

    const subscribeLeadTagsChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return null;
      }

      return subscribePostgresChannelMulti(channelName, [
        {
          binding: {
            event: "UPDATE",
            schema: "public",
            table: "leads",
            filter: `workspace_id=eq.${workspaceId}`,
          },
          callback: (payload) => {
            const row = payload.new as { id?: string; assigned_member_id?: string | null } | null;
            if (!row?.id || !("assigned_member_id" in (payload.new as object))) return;
            const oldAssigned =
              (payload.old as { assigned_member_id?: string | null } | null)?.assigned_member_id ??
              null;
            const newAssigned = row.assigned_member_id ?? null;
            if (oldAssigned === newAssigned) return;
            dispatchInboxScopeChanged({
              workspaceId,
              leadId: row.id,
              reason: "assignment",
            });
            debouncedLeadPatch(() => {
              void refreshLeadAssignmentInList(row.id!);
            });
          },
        },
        {
          binding: {
            event: "*",
            schema: "public",
            table: "lead_member_assignments",
            filter: `workspace_id=eq.${workspaceId}`,
          },
          callback: (payload) => {
            const leadId =
              (payload.new as { lead_id?: string } | null)?.lead_id ??
              (payload.old as { lead_id?: string } | null)?.lead_id;
            if (!leadId) return;
            dispatchInboxScopeChanged({
              workspaceId,
              leadId,
              reason: "assignment",
            });
            debouncedLeadPatch(() => {
              void refreshLeadAssignmentInList(leadId);
            });
          },
        },
        {
          binding: {
            event: "*",
            schema: "public",
            table: "lead_tag_assignments",
          },
          callback: (payload) => {
            const leadId =
              (payload.new as { lead_id?: string } | null)?.lead_id ??
              (payload.old as { lead_id?: string } | null)?.lead_id;
            if (!leadId) return;
            debouncedLeadPatch(() => {
              void refreshLeadTagsInList(leadId);
            });
          },
        },
      ]);
    };

    let leadTagsChannel: ReturnType<typeof subscribePostgresChannelMulti> | null =
      subscribeLeadTagsChannel();

    const teardownLeadTagsChannel = () => {
      if (!leadTagsChannel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(leadTagsChannel);
      leadTagsChannel = null;
    };

  const reconcileFromBackground = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (!leadTagsChannel) {
        leadTagsChannel = subscribeLeadTagsChannel();
      }
      // Lista e counts são ciclos independentes: um reconcile dispara um de cada.
      reconcileFetch();
      void refreshInboxCountsRef.current();
    };
    reconcileFromBackgroundRef.current = reconcileFromBackground;

    const unsubscribeVisibilityPause = subscribeVisibilityPause(REALTIME_PAUSE_ON_HIDDEN_ENABLED, {
      onPause: teardownLeadTagsChannel,
      onResume: () => {
        // Pause-on-hidden: hub reconcile faz fetch+counts; aqui só reabre o canal de lead-tags.
        if (!leadTagsChannel) {
          leadTagsChannel = subscribeLeadTagsChannel();
        }
      },
    }, INBOX_HIDDEN_GRACE_MS);

    // Pause desligado: hub não reconcilia no resume — visibilitychange é o único caminho.
    let removeVisibilityReconcile: (() => void) | undefined;
    if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined") {
      const onVisibilityChange = () => {
        if (!document.hidden) reconcileFromBackground();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      removeVisibilityReconcile = () =>
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      unsubscribeVisibilityPause();
      removeVisibilityReconcile?.();
      teardownLeadTagsChannel();
      clearPromotionQueue();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      if (leadPatchDebounceRef.current) clearTimeout(leadPatchDebounceRef.current);
    };
  }, [
    workspaceId,
    channel,
    refreshKey,
    reloadNonce,
    integrationId,
    statusScope,
    isSearchActive,
    trimmedSearch,
    integrationAccessResolved,
    integrationAccessFailed,
    allowedIntegrationIdsKey,
    refreshLeadAssignmentInList,
    refreshLeadTagsInList,
    clearPromotionQueue,
  ]);


  useEffect(() => {
    if (!workspaceId || isSearchActive) return;
    void refreshInboxCounts();
  }, [workspaceId, statusScope, refreshInboxCounts, isSearchActive]);

  // Aplicar filtros (na busca por nome/telefone, só visibilidade — não restringir por aba/filtros ativos)
  const filteredConversations = conversations.filter((conv) => {
    // Visibilidade por atribuição (espelha a RLS — evita flicker se o realtime trouxer algo a mais)
    if (!isVisibleToMember(conv)) return false;

    if (isSearchMode) {
      return isConversationRelevantForListFilter(conv, channel, integrationId);
    }

    if (!matchesStatusFilter(conv, statusScope)) {
      return false;
    }

    if (!matchesAttentionFilter(conv, attentionFilter, currentMemberId)) {
      return false;
    }

    if (selectedTagIds.length > 0) {
      const tagIds = conv.tagIds || [];
      if (!selectedTagIds.some((id) => tagIds.includes(id))) return false;
    }

    if (filterUnreadOnly && !(conv.unreadCount && conv.unreadCount > 0)) {
      return false;
    }

    if (filterUpcomingAppointments || filterCancelledAppointments) {
      const matchesUpcoming = filterUpcomingAppointments && conv.hasUpcomingAppointment;
      const matchesCancelled = filterCancelledAppointments && conv.hasLastCancelledAppointment;
      if (!matchesUpcoming && !matchesCancelled) return false;
    }

    return true;
  });

  const sortedConversations = useMemo(() => [...filteredConversations].sort((a, b) => {
    const aPinned = pinnedConversationIds.has(a.conversation_id);
    const bPinned = pinnedConversationIds.has(b.conversation_id);

    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    if (prioritizeUnread) {
      const aUnread = (a.unreadCount || 0) > 0;
      const bUnread = (b.unreadCount || 0) > 0;

      if (aUnread !== bUnread) return aUnread ? -1 : 1;
    }

    return 0;
  }), [filteredConversations, pinnedConversationIds, prioritizeUnread]);

  const visibleSelectableIds = sortedConversations.map((c) => c.conversation_id);

  useEffect(() => {
    onVisibleSelectableIdsChange?.(visibleSelectableIds);
  }, [visibleSelectableIds, onVisibleSelectableIdsChange]);

  useEffect(() => {
    if (!onSelectedConversationsChange) return;
    const selected = sortedConversations.filter((c) =>
      selectedConversationIds.has(c.conversation_id),
    );
    onSelectedConversationsChange(selected);
  }, [sortedConversations, selectedConversationIds, onSelectedConversationsChange]);

  const remainingToLoad =
    listTotalCount != null ? Math.max(0, listTotalCount - conversations.length) : null;

  // Coerência com o contador: enquanto houver conversas contadas e não carregadas,
  // o botão continua disponível — sem consulta extra (o total já vem dos counts).
  const hasClientOnlyFilters =
    selectedTagIds.length > 0 ||
    filterUnreadOnly ||
    filterUpcomingAppointments ||
    filterCancelledAppointments;
  const canLoadMore =
    hasMore || (!hasClientOnlyFilters && remainingToLoad != null && remainingToLoad > 0);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-primary/50 animate-pulse mx-auto mb-3"></div>
          <div className="text-muted-foreground text-sm">Carregando conversas...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
        <h3 className="text-lg font-semibold">Erro ao carregar conversas</h3>
        <p className="text-muted-foreground text-sm max-w-xs">{loadError}</p>
        <Button variant="outline" size="sm" onClick={() => setReloadNonce((n) => n + 1)}>
          Tentar novamente
        </Button>
      </div>
    );
  }



  if (filteredConversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <MessageSquare className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">
          {isSearchMode || attentionFilter !== 'all' || filterUpcomingAppointments || filterCancelledAppointments || filterUnreadOnly
            ? 'Nenhum resultado encontrado'
            : 'Nenhuma conversa'}
        </h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          {isSearchMode
            ? `Nenhuma conversa encontrada para "${trimmedSearch}".`
            : attentionFilter !== 'all' || filterUpcomingAppointments || filterCancelledAppointments || filterUnreadOnly
              ? 'Tente ajustar seus filtros de busca'
              : 'Suas conversas aparecerão aqui quando você receber mensagens'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {sortedConversations.map((conv) => {
        const isSelected = selectedConversationId === conv.conversation_id;
        const isBulkSelected = selectedConversationIds.has(conv.conversation_id);
        const isPinned = pinnedConversationIds.has(conv.conversation_id);
        const handoffAttention = needsHandoffAttention(conv);
        const iaIndicator =
          !conv.escalated_by_agent_at && conv.state === "active"
            ? IA_STATE_INDICATORS.active
            : !conv.escalated_by_agent_at && conv.state === "paused"
              ? IA_STATE_INDICATORS.paused
              : null;
        const statusBadge = conv.escalated_by_agent_at ? HANDOFF_BADGE : null;

        return (
          <Card
            key={conv.conversation_id}
            className={cn(
              "group relative cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary/30",
              isSelected && !selectionMode
                ? "ring-1 ring-primary border-primary/50 bg-primary/5"
                : selectionMode && isBulkSelected
                  ? "ring-1 ring-primary border-primary/40 bg-primary/5"
                : isPinned
                  ? "border-primary/30 bg-primary/5"
                  : "hover:bg-muted/20",
              handoffAttention && !isSelected && !isBulkSelected && "border-amber-500/40"
            )}
            onClick={() => {
              if (selectionMode) {
                onToggleConversationSelection?.(conv);
                return;
              }

              // Registrar timestamp de leitura persistente
              readTimestamps.current.set(conv.conversation_id, new Date().toISOString());
              try {
                localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(readTimestamps.current)));
              } catch {}
              
              // Atualização otimista: marcar como lida imediatamente
              if (conv.unreadCount && conv.unreadCount > 0) {
                setConversations(prev => prev.map(c => 
                  c.conversation_id === conv.conversation_id 
                    ? { ...c, isUnread: false, unreadCount: 0 } 
                    : c
                ));
                setServerFilterCounts((prev) => {
                  if (!prev || prev.unread == null) return prev;
                  return { ...prev, unread: Math.max(0, prev.unread - 1) };
                });
                
                // Reset unread_count no DB (fire and forget; overlay localStorage é cache)
                void markConversationRead(conv.conversation_id);
              }

              // Um único caminho de sync de badge (patch local + reconcile debounce em useUnreadNotifications)
              window.dispatchEvent(new CustomEvent('conversation-read', {
                detail: { conversationId: conv.conversation_id }
              }));

              onSelectConversation(conv);
            }}
          >
            {handoffAttention && !isSelected && (
              <span
                className="pointer-events-none absolute inset-0 z-[1] rounded-lg ring-2 ring-amber-500/80 animate-pulse"
                aria-hidden
              />
            )}
            <CardContent className="relative z-0 p-2 sm:p-2.5">
              <div className="flex items-start gap-2.5">
                {selectionMode && (
                  <div
                    className="flex shrink-0 items-center pt-1"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={isBulkSelected}
                      onCheckedChange={() => onToggleConversationSelection?.(conv)}
                      aria-label={`Selecionar conversa com ${conv.lead_name}`}
                    />
                  </div>
                )}
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                    <AvatarImage 
                      src={(conv.metadata as any)?.instagram_profile_pic || (conv.metadata as any)?.profile_picture} 
                      alt={conv.lead_name} 
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className="object-cover" 
                    />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/50 text-white font-semibold text-sm">
                      {conv.lead_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="mb-0.5 flex items-center justify-between gap-1">
                    <div className="flex min-w-0 items-center gap-1">
                      <h4 className="truncate text-sm font-medium">
                        {conv.lead_name}
                      </h4>
                      {iaIndicator && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              aria-label={iaIndicator.label}
                              className={cn(
                                "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                                iaIndicator.shell,
                              )}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Bot
                                className={cn("h-3 w-3", iaIndicator.icon)}
                                strokeWidth={2.25}
                                aria-hidden
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">{iaIndicator.label}</TooltipContent>
                        </Tooltip>
                      )}
                      {statusBadge && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${statusBadge.className}`}
                        >
                          {statusBadge.label}
                        </span>
                      )}
                      {conv.hasUpcomingAppointment && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              aria-label="Agendamento futuro"
                              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500/10"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Calendar className="h-3 w-3 text-green-600" aria-hidden />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">Agendamento futuro</TooltipContent>
                        </Tooltip>
                      )}
                      {!conv.hasUpcomingAppointment && conv.hasLastCancelledAppointment && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              aria-label="Último registro do lead é um cancelamento"
                              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-500/10"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Calendar className="h-3 w-3 text-orange-600" aria-hidden />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Último registro do lead é um cancelamento
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {conv.isUrgent && (
                        <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="ml-1 flex shrink-0 items-center gap-1">
                      {conv.unreadCount && conv.unreadCount > 0 ? (
                        <div className="bg-green-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                          {conv.unreadCount}
                        </div>
                      ) : null}
                      <span className="text-xs text-muted-foreground">
                        {formatConversationDate(conv.lastMessageAt || conv.updated_at)}
                      </span>
                      {isPinned && (
                        <Pin
                          className="h-3 w-3 shrink-0 fill-current text-primary"
                          aria-label="Conversa fixada"
                        />
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Opções da conversa"
                            onClick={(event) => event.stopPropagation()}
                            className="flex h-6 w-6 items-center justify-center rounded-md border border-border/50 bg-muted/30 text-muted-foreground opacity-0 transition-opacity hover:bg-muted/60 hover:text-foreground group-hover:opacity-100 data-[state=open]:opacity-100"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="min-w-[180px]"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <DropdownMenuItem
                            onSelect={() => onEnterSelectionWithConversation?.(conv)}
                          >
                            <CheckSquare className="mr-2 h-4 w-4" />
                            Selecionar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              togglePinnedConversation(conv.conversation_id)
                            }
                          >
                            <Pin
                              className={`mr-2 h-4 w-4 ${isPinned ? "fill-current" : ""}`}
                            />
                            {isPinned ? "Desafixar" : "Fixar"}
                          </DropdownMenuItem>
                          {onRequestClearContext ? (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() =>
                                onRequestClearContext(conv.conversation_id)
                              }
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Limpar conversa
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            onSelect={() => void markConversationAsUnread(conv)}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            Marcar como não lida
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {conv.lastMessageBody ? (
                    <p className="text-xs text-muted-foreground line-clamp-2 break-words hyphens-auto">
                      {conv.lastMessageFrom === "assistant" && (
                        <span className="text-muted-foreground/70">Você: </span>
                      )}
                      {stripMarkdown(conv.lastMessageBody)}
                    </p>
                  ) : conv.lead_phone ? (
                    <p className="text-xs text-muted-foreground truncate">
                      {conv.lead_phone}
                    </p>
                  ) : null}
                  {(conv.assignedMemberLabel || (conv.leadTags?.length ?? 0) > 0) && (
                    <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground/80">
                      {conv.assignedMemberLabel ? (
                        <span
                          className="inline-flex min-w-0 max-w-[min(100%,12rem)] items-center gap-1 truncate"
                          title={conv.assignedMemberLabel}
                        >
                          <UserRound
                            className="h-3 w-3 shrink-0 text-primary"
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span className="truncate font-medium text-primary">
                            {conv.assignedMemberLabel}
                          </span>
                        </span>
                      ) : null}
                      {(conv.leadTags?.length ?? 0) > 0 ? (
                        <div className="flex flex-wrap items-center gap-0.5">
                          {conv.leadTags!.map((tag) => (
                            <LeadTagBadge key={tag.id} tag={tag} size="sm" />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {canLoadMore && !isSearchMode && (
        <div className="flex justify-center py-3">
          <button
            onClick={() => {
              setLoadingMore(true);
              const fetchAll = async () => {
                const lastConv = conversations[conversations.length - 1];
                const pageRows = await fetchInboxPageRows(supabase, {
                  workspaceId,
                  channel,
                  integrationId,
                  isClosed: statusScope ? statusScope === "closed" : null,
                  before: lastConv
                    ? {
                        lastMessageAt: lastConv.lastMessageAt ?? null,
                        id: lastConv.conversation_id,
                      }
                    : null,
                  limit: PAGE_SIZE,
                });

                let data: unknown[] | null = pageRows;
                if (!pageRows) {
                  let query = supabase
                    .from("conversations")
                    .select(CONVERSATION_LIST_SELECT)
                    .eq("workspace_id", workspaceId);

                  query = applyConversationListFilters(
                    query as any,
                    channel,
                    integrationId,
                    allowedIntegrationIdsRef.current,
                  ) as any;

                  if (statusScope) {
                    query = query.eq("is_closed", statusScope === "closed") as any;
                  }

                  // Keyset composto (last_message_at, id): sem saltos nem duplicatas
                  // quando novas mensagens reordenam a lista durante a paginação.
                  if (lastConv) {
                    query = query.or(
                      buildInboxKeysetOrFilter({
                        lastMessageAt: lastConv.lastMessageAt ?? null,
                        id: lastConv.conversation_id,
                      }),
                    ) as any;
                  }

                  const result = await query
                    .order("last_message_at", { ascending: false, nullsFirst: false })
                    .order("id", { ascending: false })
                    .limit(PAGE_SIZE);
                  data = result.data;
                }

                if (data && data.length > 0) {
                  const mapped = data
                    .map((row) =>
                      mapRawConversationRow(row as Record<string, unknown>, readTimestamps.current),
                    )
                    .filter(Boolean) as Conversation[];

                  const enriched = await enrichConversationsList(supabase, mapped, workspaceId);
                  setConversations((prev) => {
                    const merged = mergeInboxKeysetPages(prev, enriched);
                    conversationsRef.current = merged;
                    return merged;
                  });
                  const nextHasMore = mapped.length >= PAGE_SIZE;
                  hasMoreRef.current = nextHasMore;
                  setHasMore(nextHasMore);
                } else {
                  hasMoreRef.current = false;
                  setHasMore(false);
                }
                setLoadingMore(false);
              };
              fetchAll();
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors px-4 py-2"
            disabled={loadingMore}
          >
            {loadingMore
              ? "Carregando..."
              : remainingToLoad != null && remainingToLoad > 0
                ? `Carregar mais conversas (${remainingToLoad} restantes)`
                : "Carregar mais conversas"}
          </button>
        </div>
      )}
    </div>
  );
}
